import { FieldArray, useFormikContext } from "formik";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type Form8821Data,
  type Form8821Matter,
  MAX_TIA_MATTERS,
} from "@/features/forms/types";
import { FormikCell } from "./formik-controls";

const EMPTY_MATTER: Form8821Matter = {
  tax_type: "",
  tax_form: "",
  tax_years: "",
  tax_matters: "",
};

/**
 * Form 8821 Section 3 "Tax Information" rows — four columns (Type of Tax, Tax
 * Form Number, Year(s) or Period(s), Specific Tax Matters), capped at the
 * template's three body rows. Distinct from the 2848 matters table (no
 * ownership column; 8821 adds a specific-matters column).
 */
export function TiaMattersTable() {
  const { values } = useFormikContext<Form8821Data>();
  const matters = values.matters;

  return (
    <FieldArray name="matters">
      {(helpers) => (
        <div className="flex flex-col gap-3">
          <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type of Tax Information</TableHead>
                  <TableHead className="w-36">Tax Form Number</TableHead>
                  <TableHead className="w-44">Year(s) or Period(s)</TableHead>
                  <TableHead>Specific Tax Matters</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {matters.map((_matter, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.tax_type`}
                        placeholder="e.g. Income"
                        aria-label={`Type of tax for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.tax_form`}
                        placeholder="e.g. 1040"
                        aria-label={`Tax form for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.tax_years`}
                        placeholder="e.g. 2021, 2022"
                        aria-label={`Years for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <FormikCell
                        name={`matters.${i}.tax_matters`}
                        placeholder="Optional"
                        aria-label={`Specific matters for matter ${i + 1}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove matter ${i + 1}`}
                        disabled={matters.length === 1}
                        onClick={() => helpers.remove(i)}
                      >
                        <Trash2 className="text-muted-foreground size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={matters.length >= MAX_TIA_MATTERS}
              onClick={() => helpers.push({ ...EMPTY_MATTER })}
            >
              <Plus /> Add tax matter
            </Button>
            {matters.length >= MAX_TIA_MATTERS && (
              <span className="text-muted-foreground ml-3 text-xs">
                Form 8821 fits up to {MAX_TIA_MATTERS} matters.
              </span>
            )}
          </div>
        </div>
      )}
    </FieldArray>
  );
}
